import jsPDF from 'jspdf';
import i18n from '../../../i18n';
import { SceneWithUserNames } from '../../../types/scene.types';
import { BreakdownInfo } from '../../../pages/Home/types';
import {
  ElementInfo,
  ElementType,
  ElementWithUserNames,
  ElementSceneInfo,
} from '../../../types/element.types';
import { ElementDataCache, ElementRelationDataCache, BreakdownDataCache } from '../../../types/cache.types';
import { sortCastItemsByNO } from '../../../utils/elementSortUtils';
import { sortScenes } from '../../../pages/Breakdown/BreakdownMain/Utils/SceneCodeUtils';
import { registerFont, setFont, SupportedLanguage } from '../PDFExporter';
import { formatDayNightForExport, formatInteriorExteriorForExport } from '../exportSceneFieldFormat';

// @ts-ignore
const autoTable = require('jspdf-autotable').default;

const t = (key: string, opts?: { lng?: string; [k: string]: unknown }) => i18n.t(key, opts);

const PAGE = {
  MARGIN: 10,
  HEADER_Y: 9,
  START_Y: 15,
};

export const PDF_CAST_BREAKDOWN_A4_MARGINS_MM = {
  left: PAGE.MARGIN,
  right: PAGE.MARGIN,
  top: PAGE.START_Y,
  bottom: PAGE.MARGIN
} as const;

const CELL_PAD = 0.8;
/** 与 autoTable cellPadding left/right 一致 */
const CELL_PAD_X = 1;
const TABLE_FONT_SIZE = 9;
const PLOT_DAY_MAX_LINES = 2;
/** 场号、内外、日夜、场景、剧情日、剧情、主要角色 */
const PLOT_DAY_COL_INDEX = 4;

const FONT_FILES: Record<SupportedLanguage, { name: string }> = {
  'zh-CN': { name: 'AlibabaPuHuiTi' },
  'zh-HK': { name: 'AlibabaPuHuiTi' },
  en: { name: 'AlibabaPuHuiTi' },
};

/** 原六列基准宽度（mm），合计 190 = A4 竖版内容区；剧情日从场景/剧情/主要角色匀出 */
const COL = {
  code: 18,
  io: 12,
  dn: 12,
  set: 44,
  plot: 60,
  /** 主要角色简称（原拍摄地点列宽） */
  cast: 44,
};

function truncateNoEllipsis(
  doc: jsPDF,
  text: string,
  maxW: number,
  fontName: string,
  fontSize: number,
  fontStyle: 'normal' | 'bold' = 'normal'
): string {
  const src = (text || '').replace(/\r?\n/g, ' ');
  if (!src) return '';
  doc.setFont(fontName, fontStyle);
  doc.setFontSize(fontSize);
  if (doc.getTextWidth(src) <= maxW) return src;
  let lo = 0;
  let hi = src.length;
  let best = '';
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const sub = src.slice(0, mid);
    if (doc.getTextWidth(sub) <= maxW) {
      best = sub;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

/** 列内容区可用宽度（mm），用于表体截断 */
function colInnerWidthMm(cellWidthMm: number): number {
  return Math.max(0, cellWidthMm - CELL_PAD_X * 2);
}

function wrapTextToMaxLinesNoEllipsis(
  doc: jsPDF,
  raw: string,
  maxW: number,
  fontName: string,
  fontSizePt: number,
  maxLines: number,
  fontStyle: 'normal' | 'bold' = 'normal'
): string[] {
  if (maxW <= 0 || maxLines <= 0) return [];
  const normalized = (raw || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (!normalized) return [];
  doc.setFont(fontName, fontStyle);
  doc.setFontSize(fontSizePt);
  const wrapped = doc.splitTextToSize(normalized, maxW) as string[];
  if (wrapped.length <= maxLines) return wrapped;
  const lines = wrapped.slice(0, maxLines);
  lines[maxLines - 1] = truncateNoEllipsis(
    doc,
    lines[maxLines - 1] || '',
    maxW,
    fontName,
    fontSizePt,
    fontStyle
  );
  return lines;
}

/** 单行可容纳表头「剧情日」与 5 个数字的列宽（含左右内边距） */
function plotDayColumnWidthMm(
  doc: jsPDF,
  fontName: string,
  fontSizePt: number,
  headerText: string
): number {
  doc.setFontSize(fontSizePt);
  doc.setFont(fontName, 'normal');
  let digitW = 0;
  for (const d of '0123456789') {
    digitW = Math.max(digitW, doc.getTextWidth(d));
  }
  const fiveDigitsInner = digitW * 5;
  doc.setFont(fontName, 'bold');
  const headerInner = doc.getTextWidth(headerText || '');
  doc.setFont(fontName, 'normal');
  return Math.max(fiveDigitsInner, headerInner) + CELL_PAD_X * 2 + 0.3;
}

function normalizeCellText(value: string): string {
  return (value || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

const getFieldMapForLanguage = (language: SupportedLanguage) => ({
  sceneCode: t('export.sceneCode', { lng: language }),
  interiorExterior: t('export.interiorExterior', { lng: language }),
  dayNight: t('export.dayNight', { lng: language }),
  sceneSet: t('export.sceneSet', { lng: language }),
  scenePlotDay: t('export.scenePlotDay', { lng: language }),
  scenePlot: t('export.scenePlot', { lng: language }),
  cast: t('export.cast', { lng: language }),
});

function getTableColumnWidthsMm(plotDayMm: number): number[] {
  const pool = COL.set + COL.plot + COL.cast;
  const take = Math.min(Math.max(0, plotDayMm), pool * 0.45);
  const set = COL.set - (take * COL.set) / pool;
  const plot = COL.plot - (take * COL.plot) / pool;
  const used = COL.code + COL.io + COL.dn + set + take + plot;
  const cast = 190 - used;
  return [COL.code, COL.io, COL.dn, set, take, plot, cast];
}

/** 某场次已关联的主要角色简称串（CAST，按 NO 排序；无简称则用名称） */
function buildSceneCastAbbrevLine(
  sceneId: string,
  allRelations: ElementSceneInfo[],
  allElements: ElementInfo[]
): string {
  const linked = new Set(allRelations.filter((r) => r.sceneID === sceneId).map((r) => r.elementID));
  const castItems = allElements.filter(
    (it) => it.elementType === ElementType.CAST && linked.has(it.elementID)
  ) as ElementWithUserNames[];
  const sorted = sortCastItemsByNO(castItems);
  return sorted
    .map((it) => (it.elementAbbr || it.elementName || '').trim())
    .filter(Boolean)
    .join('/');
}

export interface ExportCastBreakdownParams {
  language: SupportedLanguage;
  secondaryLanguage?: SupportedLanguage;
  breakdownInfo: BreakdownInfo;
  breakdownId: string;
  getElementCache?: (breakdownId: string) => ElementDataCache | null;
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null;
  getBreakdownCache?: (breakdownId: string) => BreakdownDataCache | null;
  optionName?: string;
  onProgress?: (n: number) => void;
}

function paintCastBreakdownPdfHeader(
  doc: jsPDF,
  language: SupportedLanguage,
  breakdownInfo: BreakdownInfo,
  roleDisplayName: string,
  fontName: string
) {
  const pageW = doc.internal.pageSize.getWidth();
  const leftX = PAGE.MARGIN;
  const rightX = pageW - PAGE.MARGIN;
  const fs = 9;
  doc.setFontSize(fs);
  setFont(doc, language, 'normal');
  doc.setFont(fontName, 'normal');
  doc.setTextColor(0, 0, 0);

  const bar = '\uff5c';
  const suffix = t('export.castSceneListSuffix', { lng: language });
  const leftFull = `${roleDisplayName}${bar}${suffix}`;
  const leftMax = pageW * 0.34 - PAGE.MARGIN;
  const leftText = truncateNoEllipsis(doc, leftFull, Math.max(28, leftMax), fontName, fs);
  doc.text(leftText, leftX, PAGE.HEADER_Y);
  const wLeftDrawn = doc.getTextWidth(leftText);
  const scriptGapMm = 5;
  const xScript = leftX + wLeftDrawn + scriptGapMm;

  const scriptLine = t('export.castPdfScriptVersionLine', {
    lng: language,
    label: t('export.scriptVersion', { lng: language }),
    value: breakdownInfo.breakdownScriptOverview || '',
  });

  const breakdownName = (breakdownInfo.breakdownName || '').trim();
  const rightFull = t('export.castPdfHeaderRightBreakdownOnly', {
    lng: language,
    label: t('export.castPdfBreakdownVersionLabel', { lng: language }),
    value: breakdownName,
  });

  const rightMaxW = Math.max(36, pageW * 0.38);
  const rightText = truncateNoEllipsis(doc, rightFull, rightMaxW, fontName, fs);
  const wRight = doc.getTextWidth(rightText);

  const scriptMax = Math.max(20, rightX - wRight - 4 - xScript);
  const scriptText = truncateNoEllipsis(doc, scriptLine, scriptMax, fontName, fs);
  doc.text(scriptText, xScript, PAGE.HEADER_Y);

  doc.text(rightText, rightX, PAGE.HEADER_Y, { align: 'right' });
}

export async function exportCastBreakdownA4Document(
  initialDoc: jsPDF,
  params: ExportCastBreakdownParams
): Promise<jsPDF> {
  const { language, breakdownInfo, breakdownId, getElementCache, getElementRelationCache, getBreakdownCache, onProgress } =
    params;
  const secondaryLanguage = params.secondaryLanguage;

  void initialDoc;

  const doc = new jsPDF('p', 'mm', 'a4');
  const fontRegistered = await registerFont(doc, language);
  setFont(doc, language);
  const fontName = fontRegistered ? FONT_FILES[language].name : 'helvetica';
  onProgress?.(12);

  const elements = getElementCache?.(breakdownId)?.elements ?? [];
  const relations = getElementRelationCache?.(breakdownId)?.elementRelations ?? [];
  const allScenes = getBreakdownCache?.(breakdownId)?.scenes ?? [];

  const sortedCastItems = sortCastItemsByNO(
    elements.filter((it) => it.elementType === ElementType.CAST) as ElementWithUserNames[]
  );

  const sceneById = new Map<string, SceneWithUserNames>();
  allScenes.forEach((s) => sceneById.set(s.sceneID, s));

  const fieldMap = getFieldMapForLanguage(language);
  const colWidths = getTableColumnWidthsMm(
    plotDayColumnWidthMm(doc, fontName, TABLE_FONT_SIZE, fieldMap.scenePlotDay)
  );
  const headRow = [
    fieldMap.sceneCode,
    fieldMap.interiorExterior,
    fieldMap.dayNight,
    fieldMap.sceneSet,
    fieldMap.scenePlotDay,
    fieldMap.scenePlot,
    fieldMap.cast,
  ];

  let headerRoleName = '';

  const redrawHeader = () => {
    paintCastBreakdownPdfHeader(doc, language, breakdownInfo, headerRoleName, fontName);
  };

  const colCount = headRow.length;
  const emptyRowPlaceholders = Array.from({ length: colCount }, () => '') as string[];

  const formatHeadCell = (h: string, i: number) => {
    const inner = colInnerWidthMm(colWidths[i] ?? 18);
    if (i === PLOT_DAY_COL_INDEX) {
      return wrapTextToMaxLinesNoEllipsis(
        doc,
        h,
        inner,
        fontName,
        TABLE_FONT_SIZE,
        PLOT_DAY_MAX_LINES,
        'bold'
      ).join('\n');
    }
    return truncateNoEllipsis(doc, h, inner, fontName, TABLE_FONT_SIZE, 'bold');
  };
  const headTruncated = headRow.map(formatHeadCell);

  if (sortedCastItems.length === 0) {
    headerRoleName = '';
    redrawHeader();
    autoTable(doc, {
      startY: PAGE.START_Y,
      theme: 'grid',
      margin: { left: PAGE.MARGIN, right: PAGE.MARGIN, top: PAGE.START_Y, bottom: PAGE.MARGIN },
      showHead: 'everyPage',
      head: [headTruncated],
      body: [
        [
          {
            content: t('export.noCastToExport', { lng: language }),
            colSpan: colCount,
            styles: { halign: 'left', overflow: 'linebreak', minCellHeight: 5.8, valign: 'top' },
          },
          ...emptyRowPlaceholders.slice(1),
        ],
      ],
      styles: {
        font: fontName,
        fontSize: TABLE_FONT_SIZE,
        cellPadding: { top: CELL_PAD, right: 1, bottom: CELL_PAD, left: 1 },
        overflow: 'linebreak',
        valign: 'top',
        textColor: 0,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      headStyles: {
        font: fontName,
        fontStyle: 'bold',
        fillColor: [245, 245, 245],
        textColor: 0,
        halign: 'left',
        valign: 'top',
        overflow: 'linebreak',
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      columnStyles: Object.fromEntries(colWidths.map((w, i) => [i, { cellWidth: w, overflow: 'linebreak' }])),
      didDrawPage: () => redrawHeader(),
    });
    onProgress?.(90);
    return doc;
  }

  const total = sortedCastItems.length;
  sortedCastItems.forEach((castItem: ElementInfo, idx: number) => {
    if (idx > 0) {
      doc.addPage();
    }
    headerRoleName = (castItem.elementName || castItem.elementAbbr || '').trim();
    redrawHeader();

    const relForCast = relations.filter((r) => r.elementID === castItem.elementID);
    const sceneIds = Array.from(new Set(relForCast.map((r) => r.sceneID)));
    const scenesRows = sceneIds.map((id) => sceneById.get(id)).filter((s): s is SceneWithUserNames => Boolean(s));
    const scenesSorted = sortScenes(scenesRows, 'sceneCode', 'asc');

    const emptyMsg = t('export.castPdfNoBreakdownForRole', { lng: language });

    let listBody: any[];
    if (scenesSorted.length === 0) {
      listBody = [
        [
          {
            content: emptyMsg,
            colSpan: colCount,
            styles: { halign: 'left', overflow: 'linebreak', minCellHeight: 5.8, valign: 'top' },
          },
          ...emptyRowPlaceholders.slice(1),
        ],
      ];
    } else {
      listBody = scenesSorted.map((scene) => {
        const code = normalizeCellText(scene.sceneCode || '');
        const io = normalizeCellText(
          formatInteriorExteriorForExport(language, scene.sceneInteriorExterior, secondaryLanguage)
        );
        const dn = normalizeCellText(formatDayNightForExport(language, scene.sceneDayNight, secondaryLanguage));
        const setT = normalizeCellText(scene.sceneSet || '');
        const plotDayInner = colInnerWidthMm(colWidths[PLOT_DAY_COL_INDEX] ?? 18);
        const plotDayT = wrapTextToMaxLinesNoEllipsis(
          doc,
          normalizeCellText(scene.scenePlotDay || ''),
          plotDayInner,
          fontName,
          TABLE_FONT_SIZE,
          PLOT_DAY_MAX_LINES
        ).join('\n');
        const plotT = normalizeCellText(scene.scenePlot || '');
        const castLine = buildSceneCastAbbrevLine(scene.sceneID, relations, elements);
        const castT = normalizeCellText(castLine);

        return [
          { content: code, styles: { overflow: 'linebreak' as const, halign: 'left' as const, valign: 'top' as const } },
          { content: io, styles: { overflow: 'linebreak' as const, halign: 'left' as const, valign: 'top' as const } },
          { content: dn, styles: { overflow: 'linebreak' as const, halign: 'left' as const, valign: 'top' as const } },
          { content: setT, styles: { overflow: 'linebreak' as const, halign: 'left' as const, valign: 'top' as const } },
          { content: plotDayT, styles: { overflow: 'linebreak' as const, halign: 'left' as const, valign: 'top' as const } },
          { content: plotT, styles: { overflow: 'linebreak' as const, halign: 'left' as const, valign: 'top' as const } },
          { content: castT, styles: { overflow: 'linebreak' as const, halign: 'left' as const, valign: 'top' as const } },
        ];
      });
    }

    autoTable(doc, {
      startY: PAGE.START_Y,
      theme: 'grid',
      margin: { left: PAGE.MARGIN, right: PAGE.MARGIN, top: PAGE.START_Y, bottom: PAGE.MARGIN },
      showHead: 'everyPage',
      head: [headTruncated],
      body: listBody,
      styles: {
        font: fontName,
        fontSize: TABLE_FONT_SIZE,
        cellPadding: { top: CELL_PAD, right: 1, bottom: CELL_PAD, left: 1 },
        overflow: 'linebreak',
        valign: 'top',
        textColor: 0,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      headStyles: {
        font: fontName,
        fontStyle: 'bold',
        fillColor: [245, 245, 245],
        textColor: 0,
        halign: 'left',
        valign: 'top',
        overflow: 'linebreak',
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      columnStyles: Object.fromEntries(colWidths.map((w, i) => [i, { cellWidth: w, overflow: 'linebreak' }])),
      didDrawPage: () => redrawHeader(),
    });

    const p = 20 + Math.round(((idx + 1) / Math.max(1, total)) * 68);
    onProgress?.(Math.min(88, p));
  });

  onProgress?.(90);
  return doc;
}
